import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { normalizeHttpError } from "../../../lib/http/errors";
import type { ItineraryTransportMode } from "../../../shared/itinerary-resource-entry";
import {
  applyFulfillmentOrderChange,
  applyFinalizedCourseRouteWorkspaceChange,
  confirmCourseRouteLines,
  fetchFulfillmentWorkspaceEditor,
  finalizeCourseRouteWorkspace,
  fetchFulfillmentOrderDetail,
  generateFulfillmentOrders,
  listSupplierOptions,
  listCourseRouteWorkspaceChanges,
  saveFulfillmentWorkspaceDraft,
  searchFulfillmentTransportPoints,
  searchFulfillmentTransportResourceCandidates,
  searchFulfillmentResourceCandidates,
  toLegacyFulfillmentEditor,
  unfinalizeCourseRouteWorkspace,
} from "../repo";
import {
  createManualFulfillmentLine,
  duplicateFulfillmentLine,
  filterFulfillmentLines,
  fulfillmentLineIdentityLabel,
  isFulfillmentStayResourceLinked,
  FulfillmentOrderUserFacingError,
  fulfillmentSupplierTypesForResource,
  patchFulfillmentLine,
  sumActiveFulfillmentLineAmounts,
  sumFulfillmentAmounts,
  validateFulfillmentLineBeforeGeneration,
  validateFulfillmentLineDraft,
  validateFulfillmentLinesBeforeSave,
  validateFulfillmentLinesBeforeFinalize,
} from "../service";
import type {
  ApplyFulfillmentOrderChangeRequest,
  ConfirmationBatchChange,
  FulfillmentLineFilters,
  FulfillmentOrder,
  FulfillmentOrderLine,
  FulfillmentOrderLineAdjustment,
  FulfillmentWorkspaceEditor,
  FulfillmentResourceMentionLevel,
  FulfillmentResourceMentionType,
  FulfillmentResourceTransportMode,
  FulfillmentTransportResourceCandidate,
  GenerateSelectedFulfillmentOrdersRequest,
} from "../types";

export const fulfillmentWorkspaceQueryKey = (workspaceId: string) =>
  ["fulfillmentOrder", "workspace", workspaceId] as const;

type EditorWriteKind = "save" | "generate" | "confirm" | "finalize" | "unfinalize" | "change" | "reload";
type EditorWriteMutex = { kind: EditorWriteKind; key: string; promise: Promise<unknown> };
type EditorChangeIntent = { key: string; request: ApplyFulfillmentOrderChangeRequest };
const EDITOR_WRITE_BUSY_MESSAGE = "已有确认件写操作正在处理，请稍候。";
const EMPTY_SUPPLIER_TYPES: readonly string[] = [];

/** 生成后端幂等键；旧浏览器无 randomUUID 时仍保证单次页面会话唯一。 */
function createPostSigningIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `fulfillment-change-${uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

/** 确认件内部编辑器运行时，集中维护服务端状态、脏草稿与 CAS 写入。 */
export function useFulfillmentOrderEditorRuntime(
  workspaceId: string,
  canUpdate: boolean,
  canFinalize = false,
) {
  const queryClient = useQueryClient();
  const [settlementSupplierSearch, searchSettlementSuppliers] = useState("");
  const [quickEntrySupplierSearch, searchQuickEntryFulfillmentSuppliers] = useState("");
  const [quickEntryResourceType, setQuickEntryResourceTypeState] = useState("住宿");
  const setQuickEntryResourceType = useCallback((resourceType: string) => {
    searchQuickEntryFulfillmentSuppliers("");
    setQuickEntryResourceTypeState(resourceType);
  }, []);
  const quickEntrySupplierTypes = fulfillmentSupplierTypesForResource(quickEntryResourceType);
  const quickEntrySupplierTypesKey = quickEntrySupplierTypes ?? EMPTY_SUPPLIER_TYPES;
  const quickEntrySupplierQueryType = quickEntrySupplierTypes?.length === 1
    ? quickEntrySupplierTypes[0]
    : quickEntrySupplierTypes;
  const quickEntrySupplierSubjectNature = quickEntryResourceType === "导游"
    ? "individual" as const
    : null;
  const query = useQuery({
    queryKey: fulfillmentWorkspaceQueryKey(workspaceId),
    queryFn: () => fetchFulfillmentWorkspaceEditor(workspaceId),
    enabled: Boolean(workspaceId),
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const settlementSupplierQuery = useQuery({
    queryKey: ["fulfillmentOrder", "supplierOptions", "settlement", settlementSupplierSearch],
    queryFn: () => listSupplierOptions("settlement", settlementSupplierSearch, null),
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const quickEntrySupplierQuery = useQuery({
    queryKey: [
      "fulfillmentOrder",
      "supplierOptions",
      "fulfillment",
      quickEntrySupplierTypesKey,
      quickEntrySupplierSubjectNature,
      quickEntrySupplierSearch,
    ],
    queryFn: () => quickEntrySupplierSubjectNature
      ? listSupplierOptions(
        "fulfillment",
        quickEntrySupplierSearch,
        quickEntrySupplierQueryType,
        quickEntrySupplierSubjectNature,
      )
      : listSupplierOptions(
        "fulfillment",
        quickEntrySupplierSearch,
        quickEntrySupplierQueryType,
      ),
    placeholderData: (previousData, previousQuery) => (
      previousQuery?.queryKey[3] === quickEntrySupplierTypesKey
        && previousQuery?.queryKey[4] === quickEntrySupplierSubjectNature
        ? previousData
        : undefined
    ),
    enabled: quickEntryResourceType !== "住宿",
    retry: false,
    refetchOnWindowFocus: false,
  });
  const editor =
    query.data && !query.isPlaceholderData && query.data.workspace.id === workspaceId
      ? query.data
      : null;
  const [lines, setLines] = useState<FulfillmentOrderLine[]>([]);
  const [orders, setOrders] = useState<FulfillmentOrder[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [filters, setFilters] = useState<FulfillmentLineFilters>({
    resourceType: "all",
    assignment: "all",
    sourceDifferenceOnly: false,
  });
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedAdjustmentOrderId, setSelectedAdjustmentOrderId] = useState<string | null>(null);
  const localIdRef = useRef(0);
  const manualLineBatchRef = useRef<{
    previousDirty: boolean;
    lines: FulfillmentOrderLine[];
    ids: Set<string>;
  } | null>(null);
  const dirtyRef = useRef(false);
  const draftRevisionRef = useRef<string | null>(null);
  const requestSequenceRef = useRef(0);
  const workspaceGenerationRef = useRef(0);
  const workspaceIdRef = useRef(workspaceId);
  const lineNoHighWaterRef = useRef(0);
  const writeMutexRef = useRef<EditorWriteMutex | null>(null);
  const committedFinalizedAddRef = useRef<string | null>(null);
  const finalizedAddIntentRef = useRef<EditorChangeIntent | null>(null);
  const lineAdjustmentIntentRef = useRef<EditorChangeIntent | null>(null);
  const [writeKind, setWriteKind] = useState<EditorWriteKind | null>(null);
  workspaceIdRef.current = workspaceId;

  const searchTransportPoints = useCallback(async (
    mode: ItineraryTransportMode,
    keyword: string,
  ) => {
    const requestWorkspaceId = workspaceId;
    const requestGeneration = workspaceGenerationRef.current;
    const points = await searchFulfillmentTransportPoints(requestWorkspaceId, mode, keyword);
    if (
      workspaceIdRef.current !== requestWorkspaceId
      || workspaceGenerationRef.current !== requestGeneration
    ) return [];
    return points;
  }, [workspaceId]);

  const searchTransportResourceCandidates = useCallback(async (
    mode: FulfillmentResourceTransportMode,
    fromPointId: string,
    toPointId: string,
    specKeyword?: string | null,
  ): Promise<FulfillmentTransportResourceCandidate[]> => {
    const requestWorkspaceId = workspaceId;
    const requestGeneration = workspaceGenerationRef.current;
    const candidates = await searchFulfillmentTransportResourceCandidates(
      requestWorkspaceId,
      mode,
      fromPointId,
      toPointId,
      specKeyword,
    );
    if (
      workspaceIdRef.current !== requestWorkspaceId
      || workspaceGenerationRef.current !== requestGeneration
    ) return [];
    return candidates;
  }, [workspaceId]);

  const searchResourceCandidates = useCallback(async (
    resourceType: FulfillmentResourceMentionType,
    level: FulfillmentResourceMentionLevel,
    keyword: string,
    spuId?: string | null,
    transportMode?: FulfillmentResourceTransportMode | null,
    fulfillmentSupplierId?: string | null,
    serviceDate?: string | null,
  ) => {
    const requestWorkspaceId = workspaceId;
    const requestGeneration = workspaceGenerationRef.current;
    const candidates = serviceDate
      ? await searchFulfillmentResourceCandidates(requestWorkspaceId, resourceType, level, keyword, spuId, transportMode, fulfillmentSupplierId, 20, serviceDate)
      : fulfillmentSupplierId
      ? await searchFulfillmentResourceCandidates(
        requestWorkspaceId,
        resourceType,
        level,
        keyword,
        spuId,
        transportMode,
        fulfillmentSupplierId,
      )
      : await searchFulfillmentResourceCandidates(
        requestWorkspaceId,
        resourceType,
        level,
        keyword,
        spuId,
        transportMode,
      );
    if (
      workspaceIdRef.current !== requestWorkspaceId
      || workspaceGenerationRef.current !== requestGeneration
    ) return [];
    return candidates;
  }, [workspaceId]);

  const runWrite = useCallback(<T,>(
    kind: EditorWriteKind,
    key: string,
    operation: (isCurrent: () => boolean) => Promise<T>,
  ): Promise<T> => {
    const current = writeMutexRef.current;
    if (current) {
      if (current.kind === kind && current.key === key) return current.promise as Promise<T>;
      return Promise.reject(new FulfillmentOrderUserFacingError(EDITOR_WRITE_BUSY_MESSAGE));
    }
    // 签后操作会权威回填整池，必须先保存其他普通行，避免成功回读覆盖未提交草稿。
    if (kind === "change" && dirtyRef.current) {
      return Promise.reject(new FulfillmentOrderUserFacingError("请先保存确认行池，再进行增减"));
    }
    const targetWorkspaceId = workspaceIdRef.current;
    const targetGeneration = workspaceGenerationRef.current;
    const isCurrent = () => workspaceIdRef.current === targetWorkspaceId
      && workspaceGenerationRef.current === targetGeneration;
    const pending = Promise.resolve().then(() => operation(isCurrent));
    const tracked = pending.finally(() => {
      if (writeMutexRef.current?.promise !== tracked) return;
      writeMutexRef.current = null;
      setWriteKind(null);
    });
    writeMutexRef.current = { kind, key, promise: tracked };
    setWriteKind(kind);
    return tracked;
  }, []);

  const markDirty = useCallback(() => {
    if (!dirtyRef.current) {
      draftRevisionRef.current = editor?.workspace.revision ?? null;
      dirtyRef.current = true;
      setIsDirty(true);
    }
    setErrorCode((current) => current === "FULFILLMENT_ORDER_REVISION_CONFLICT" ? current : null);
  }, [editor]);

  useEffect(() => {
    workspaceGenerationRef.current += 1;
    setLines([]);
    setOrders([]);
    setIsDirty(false);
    dirtyRef.current = false;
    manualLineBatchRef.current = null;
    draftRevisionRef.current = null;
    requestSequenceRef.current += 1;
    lineNoHighWaterRef.current = 0;
    writeMutexRef.current = null;
    committedFinalizedAddRef.current = null;
    finalizedAddIntentRef.current = null;
    lineAdjustmentIntentRef.current = null;
    setWriteKind(null);
    setSelectedIds(new Set());
    setSelectedOrderId(null);
    setSelectedAdjustmentOrderId(null);
    setErrorCode(null);
    searchSettlementSuppliers("");
    searchQuickEntryFulfillmentSuppliers("");
    setQuickEntryResourceTypeState("住宿");
  }, [workspaceId]);

  useEffect(() => {
    if (!editor || dirtyRef.current) return;
    draftRevisionRef.current = editor.workspace.revision;
    lineNoHighWaterRef.current = Math.max(
      lineNoHighWaterRef.current,
      editor.workspace.lineNoHighWater ?? 0,
      ...editor.lines.map((line) => line.lineNo),
    );
    setLines(editor.lines);
    setOrders(editor.orders);
    // 选择范围与可编辑性一致；归入草稿协议不使原行失效。
    const draftOrderIds = new Set(editor.orders.filter(order => order.status === "draft").map(order => order.id));
    const selectableIds = new Set(editor.lines
      .filter(line => line.status === "active" && (!line.fulfillmentOrderId || draftOrderIds.has(line.fulfillmentOrderId)))
      .map(line => line.id));
    setSelectedIds(current => new Set([...current].filter(id => selectableIds.has(id))));
  }, [editor]);

  const updateLine = useCallback(
    (lineId: string, patch: Partial<FulfillmentOrderLine>) => {
      if (writeMutexRef.current || !canUpdate) return;
      const target = lines.find((line) => line.id === lineId);
      if (!target) return;
      const assignedOrder = target.fulfillmentOrderId
        ? orders.find((order) => order.id === target.fulfillmentOrderId)
        : null;
      if (target.fulfillmentOrderId && assignedOrder?.status !== "draft") return;
      try {
        const updated = patchFulfillmentLine(target, patch, Boolean(target.fulfillmentOrderId));
        setLines((current) => current.map((line) => (line.id === lineId ? updated : line)));
        markDirty();
      } catch {
        setErrorCode("FULFILLMENT_ORDER_INVALID_LINE_INPUT");
      }
    },
    [canUpdate, lines, markDirty, orders],
  );

  /** 一次应用已预览的草稿变更，校验失败时保留全部本地内容。 */
  const applyDraftBatch = useCallback((change: ConfirmationBatchChange): FulfillmentOrderLine[] => {
    if (writeMutexRef.current) throw new Error(EDITOR_WRITE_BUSY_MESSAGE);
    if (!canUpdate || !editor) throw new Error("当前确认件不可编辑。");
    if (!["draft", "ready"].includes(editor.workspace.status)) throw new Error("定稿工作区仅允许增减；此入口仅适用于草稿。");
    if (!change.updates.length && !change.additions.length && !change.removals.length) return [];
    const touched = new Set<string>();
    for (const id of [...change.updates.map(item => item.id), ...change.removals]) {
      if (touched.has(id)) throw new Error("同一明细不能在一批中重复修改或同时删除。");
      touched.add(id);
      const target = lines.find(line => line.id === id);
      if (!target || target.status !== "active") throw new Error("目标明细已变化，请重新载入编辑范围。");
      if (target.fulfillmentOrderId && orders.find(order => order.id === target.fulfillmentOrderId)?.status !== "draft") {
        throw new Error("已锁定协议中的明细不能通过草稿录入修改。");
      }
    }
    const removals = new Set(change.removals);
    for (const order of orders) {
      const assigned = lines.filter(line => line.status === "active" && line.fulfillmentOrderId === order.id);
      if (assigned.length && assigned.every(line => removals.has(line.id))) throw new Error("每份协议至少保留一条明细，不能删除最后所有归口行。");
    }
    const validateFactors = (line: FulfillmentOrderLine, newlyAdded: boolean) => {
      for (const [label, value, positive] of [
        ["数量", line.quantity, newlyAdded], ["计费次数", line.billingCount, true],
        ["单价", line.unitPriceAmount, false], ["合计", line.amount, false],
      ] as const) {
        if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) throw new Error(`${label}必须为最多两位小数的非负数字。`);
        const [whole, fraction = ""] = value.trim().split(".");
        const units = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
        if (positive && units === 0n) throw new Error(`${label}必须大于 0。`);
        if (units > 999999999999999999n) throw new Error(`${label}超出确认件金额上限。`);
      }
    };
    const updates = new Map(change.updates.map(item => [item.id, item.patch]));
    let nextLines = lines.filter(line => !removals.has(line.id)).map(line => {
      const patch = updates.get(line.id);
      if (!patch) return line;
      const updated = patchFulfillmentLine(line, { ...patch, id: line.id, workspaceId: line.workspaceId,
        fulfillmentOrderId: line.fulfillmentOrderId, sourceBudgetLineId: line.sourceBudgetLineId,
        sourceType: line.sourceType, lineNo: line.lineNo, status: line.status }, Boolean(line.fulfillmentOrderId));
      // 主体等非计费字段的批量修改不得纠正或重算历史交易金额。
      if (!["quantity", "billingCount", "unitPriceAmount"].some(field => patch[field as keyof FulfillmentOrderLine] !== undefined
        && patch[field as keyof FulfillmentOrderLine] !== line[field as keyof FulfillmentOrderLine])) updated.amount = line.amount;
      validateFactors(updated, false);
      return updated;
    });
    // 与单行删除保持一致：被拆分来源仍有多行时，不把旧差额错误归到任一行。
    const removedSources = new Set(lines.filter(line => removals.has(line.id)).map(line => line.sourceBudgetLineId).filter(Boolean));
    for (const sourceId of removedSources) {
      if (nextLines.filter(line => line.sourceBudgetLineId === sourceId).length > 1) {
        nextLines = nextLines.map(line => line.sourceBudgetLineId === sourceId ? { ...line, diffAmount: null } : line);
      }
    }
    const created = change.additions.map((patch, index) => {
      const base = createManualFulfillmentLine(editor.workspace, `local-${workspaceId}-${localIdRef.current + index + 1}`, lineNoHighWaterRef.current + index + 1);
      const line = patchFulfillmentLine(base, { ...patch, id: base.id, workspaceId: base.workspaceId,
        fulfillmentOrderId: null, sourceBudgetLineId: null, sourceType: base.sourceType, lineNo: base.lineNo, status: "active" });
      validateFactors(line, true);
      return line;
    });
    nextLines = nextLines.concat(created);
    localIdRef.current += created.length;
    lineNoHighWaterRef.current += created.length;
    manualLineBatchRef.current = null;
    setLines(nextLines);
    setSelectedIds(current => new Set([...current].filter(id => !removals.has(id))));
    markDirty();
    return created;
  }, [canUpdate, editor, lines, markDirty, orders, workspaceId]);

  const duplicateLine = useCallback(
    (lineId: string, patch: Partial<FulfillmentOrderLine> = {}) => {
      if (writeMutexRef.current || !canUpdate) return;
      const existing = lines.find((line) => line.id === lineId);
      if (!existing || existing.status !== "active") return;
      const assignedOrder = existing.fulfillmentOrderId
        ? orders.find((order) => order.id === existing.fulfillmentOrderId)
        : null;
      if (existing.fulfillmentOrderId && assignedOrder?.status !== "draft") return;
      const source = patchFulfillmentLine(existing, patch, Boolean(existing.fulfillmentOrderId));
      const copy = duplicateFulfillmentLine(source, `local-${workspaceId}-${localIdRef.current + 1}`);
      // 原历史行允许零数量，复制后的新身份必须重新校验；同步抛错供抽屉保留草稿，失败不消耗编号。
      const errors = validateFulfillmentLineDraft(copy);
      if (errors.length) throw new FulfillmentOrderUserFacingError(errors.join("；"));
      localIdRef.current += 1;
      lineNoHighWaterRef.current += 1;
      const created = { ...copy, lineNo: lineNoHighWaterRef.current };
      setLines((current) => current.concat(created));
      markDirty();
    },
    [canUpdate, lines, markDirty, orders, workspaceId],
  );

  const addManualLines = useCallback((patches: Partial<FulfillmentOrderLine>[]) => {
    if (writeMutexRef.current || !canUpdate || !editor || patches.length === 0) return [];
    const created = patches.map((patch) => {
      localIdRef.current += 1;
      lineNoHighWaterRef.current += 1;
      const base = createManualFulfillmentLine(
        editor.workspace,
        `local-${workspaceId}-${localIdRef.current}`,
        lineNoHighWaterRef.current,
      );
      return patchFulfillmentLine(base, patch);
    });
    if (!manualLineBatchRef.current) {
      manualLineBatchRef.current = {
        previousDirty: dirtyRef.current,
        lines,
        ids: new Set(),
      };
    }
    created.forEach((line) => manualLineBatchRef.current?.ids.add(line.id));
    setLines((current) => current.concat(created));
    markDirty();
    return created;
  }, [canUpdate, editor, lines, markDirty, workspaceId]);

  const addManualLine = useCallback(
    (patch: Partial<FulfillmentOrderLine> = {}) => addManualLines([patch])[0] ?? null,
    [addManualLines],
  );

  const removeLine = useCallback(
    (lineId: string) => {
      if (writeMutexRef.current || !canUpdate) return;
      const target = lines.find((line) => line.id === lineId);
      if (!target || target.status !== "active") return;
      if (target.fulfillmentOrderId) {
        const order = orders.find((candidate) => candidate.id === target.fulfillmentOrderId);
        const assignedLineCount = lines.filter(
          (line) => line.status === "active"
            && line.fulfillmentOrderId === target.fulfillmentOrderId,
        ).length;
        if (order?.status !== "draft" || assignedLineCount <= 1) return;
      }
      const withoutTarget = lines.filter((line) => line.id !== lineId);
      const sourceId = target.sourceBudgetLineId;
      const nextLines = !sourceId
        || withoutTarget.filter((line) => line.sourceBudgetLineId === sourceId).length <= 1
        ? withoutTarget
        : withoutTarget.map((line) =>
          line.sourceBudgetLineId === sourceId ? { ...line, diffAmount: null } : line,
        );
      setLines(nextLines);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(lineId);
        return next;
      });
      const manualBatch = manualLineBatchRef.current;
      if (manualBatch?.ids.has(lineId)) {
        manualBatch.ids.delete(lineId);
        if (manualBatch.ids.size === 0) {
          const returnsToBaseline = nextLines.length === manualBatch.lines.length
            && nextLines.every((line, index) => line === manualBatch.lines[index]);
          if (!manualBatch.previousDirty && returnsToBaseline) {
            dirtyRef.current = false;
            draftRevisionRef.current = null;
            setIsDirty(false);
          }
          manualLineBatchRef.current = null;
        }
        setErrorCode((current) => current === "FULFILLMENT_ORDER_REVISION_CONFLICT" ? current : null);
        return;
      }
      markDirty();
    },
    [canUpdate, lines, markDirty, orders],
  );

  const selectableLineIds = useMemo(() => {
    const draftOrderIds = new Set(
      orders.filter((order) => order.status === "draft").map((order) => order.id),
    );
    return new Set(lines
      .filter((line) => line.status === "active" && (
        !line.fulfillmentOrderId || draftOrderIds.has(line.fulfillmentOrderId)
      ))
      .map((line) => line.id));
  }, [lines, orders]);

  const setAuthoritativeEditor = useCallback((authoritative: FulfillmentWorkspaceEditor) => {
    lineNoHighWaterRef.current = Math.max(
      lineNoHighWaterRef.current,
      authoritative.workspace.lineNoHighWater ?? 0,
      ...authoritative.lines.map((line) => line.lineNo),
    );
    queryClient.setQueryData(
      fulfillmentWorkspaceQueryKey(authoritative.workspace.id),
      authoritative,
    );
    setLines(authoritative.lines);
    setOrders(authoritative.orders);
    setIsDirty(false);
    dirtyRef.current = false;
    manualLineBatchRef.current = null;
    draftRevisionRef.current = authoritative.workspace.revision;
    setSelectedIds(new Set());
    setErrorCode(null);
  }, [queryClient]);

  const reloadLatest = useCallback(() => runWrite("reload", workspaceId, async (isCurrent) => {
    const authoritative = await fetchFulfillmentWorkspaceEditor(workspaceId);
    if (!isCurrent()) return false;
    setAuthoritativeEditor(authoritative);
    await queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "detail"] });
    return isCurrent();
  }), [queryClient, runWrite, setAuthoritativeEditor, workspaceId]);

  const workspaceGeneration = workspaceGenerationRef.current;
  // 抽屉只能恢复运行时仍持有的同次新增，不能用旧本地预览绕过真正重复检查。
  const hasPendingFinalizedAddition = useCallback((patches: Partial<FulfillmentOrderLine>[]) => {
    if (workspaceIdRef.current !== workspaceId || workspaceGenerationRef.current !== workspaceGeneration) return false;
    const key = JSON.stringify([workspaceId, "add-finalized", patches]);
    return finalizedAddIntentRef.current?.key === key || committedFinalizedAddRef.current === key;
  }, [workspaceId, workspaceGeneration]);

  const addFinalizedLines = useCallback((patches: Partial<FulfillmentOrderLine>[]) => {
    const key = JSON.stringify([workspaceId, "add-finalized", patches]);
    return runWrite("change", key, async (isCurrent) => {
      if (!canUpdate || !editor || editor.workspace.status === "draft") {
        throw new FulfillmentOrderUserFacingError("确认件尚未定稿，请使用普通录入");
      }
      let currentEditor = editor;
      const committedKey = committedFinalizedAddRef.current;
      if (committedKey !== null) {
        // POST 已成功时只恢复回读；新输入须先取得最新 revision，不能复用旧提交基准。
        currentEditor = await fetchFulfillmentWorkspaceEditor(workspaceId);
        if (!isCurrent()) return currentEditor;
        committedFinalizedAddRef.current = null;
        setAuthoritativeEditor(currentEditor);
        if (committedKey === key) return currentEditor;
        if (currentEditor.workspace.status === "draft") {
          throw new FulfillmentOrderUserFacingError("确认件尚未定稿，请使用普通录入");
        }
      }
      if (finalizedAddIntentRef.current?.key !== key) {
        const additions = patches.map((patch, index) => patchFulfillmentLine(
          createManualFulfillmentLine(
            currentEditor.workspace,
            `local-finalized-${workspaceId}-${index}`,
            (currentEditor.workspace.lineNoHighWater ?? 0) + index + 1,
          ),
          patch,
        ));
        // 未收到响应不代表未落库；连同行号、资源选择冻结原请求，后台回读不能换键。
        finalizedAddIntentRef.current = {
          key,
          request: {
            expectedRevision: Number(currentEditor.workspace.revision),
            idempotencyKey: createPostSigningIdempotencyKey(),
            reason: "定稿后新增应付项目",

            addLines: structuredClone(additions),
          },
        };
      }
      const intent = finalizedAddIntentRef.current;
      try {
        await applyFinalizedCourseRouteWorkspaceChange(workspaceId, intent.request);
      } catch (error) {
        const normalized = normalizeHttpError(error);
        if (isCurrent()) {
          if (normalized.code === "FULFILLMENT_ORDER_REVISION_CONFLICT") {
            setErrorCode("FULFILLMENT_ORDER_REVISION_CONFLICT");
          }
          if (finalizedAddIntentRef.current === intent && !normalized.retriable
            && normalized.statusCode !== null && normalized.statusCode >= 400 && normalized.statusCode < 500) {
            finalizedAddIntentRef.current = null;
          }
        }
        throw error;
      }
      if (isCurrent()) {
        finalizedAddIntentRef.current = null;
        committedFinalizedAddRef.current = key;
      }
      // 历史查询独立刷新；新增只等待权威行及 revision，不让历史列表延迟阻塞录入。
      void queryClient.invalidateQueries({
        queryKey: ["fulfillmentOrder", "workspaceChanges", workspaceId],
      });
      const authoritative = await fetchFulfillmentWorkspaceEditor(workspaceId);
      if (isCurrent()) {
        committedFinalizedAddRef.current = null;
        setAuthoritativeEditor(authoritative);
      }
      return authoritative;
    });
  }, [canUpdate, editor, queryClient, runWrite, setAuthoritativeEditor, workspaceId]);

  type SaveRequest = {
    workspaceId: string;
    sequence: number;
    expectedRevision: string;
    lines: FulfillmentOrderLine[];
  };

  const saveMutation = useMutation({
    mutationFn: async (request: SaveRequest) => saveFulfillmentWorkspaceDraft(
      request.workspaceId,
      { expectedUpdatedAt: request.expectedRevision, lines: request.lines },
    ),
    onSuccess: async (saved, request) => {
      if (
        workspaceIdRef.current !== request.workspaceId
        || requestSequenceRef.current !== request.sequence
      ) return;
      const queryKey = fulfillmentWorkspaceQueryKey(request.workspaceId);
      setAuthoritativeEditor(saved);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey, exact: true }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "detail"] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "workspaceChanges", request.workspaceId] }),
      ]);
    },
    onError: (error, request) => {
      if (
        workspaceIdRef.current !== request.workspaceId
        || requestSequenceRef.current !== request.sequence
      ) return;
      const normalized = normalizeHttpError(error);
      setErrorCode(normalized.code);
    },
  });

  type GenerateRequest = {
    workspaceId: string;
    sequence: number;
    payload: GenerateSelectedFulfillmentOrdersRequest;
  };

  const generateMutation = useMutation({
    mutationFn: async (request: GenerateRequest) => generateFulfillmentOrders(
      request.workspaceId,
      request.payload,
    ),
    onSuccess: async (result, request) => {
      if (
        workspaceIdRef.current !== request.workspaceId
        || requestSequenceRef.current !== request.sequence
      ) return;
      setAuthoritativeEditor(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "detail"] }),
      ]);
    },
    onError: async (error, request) => {
      if (
        workspaceIdRef.current !== request.workspaceId
        || requestSequenceRef.current !== request.sequence
      ) return;
      const normalized = normalizeHttpError(error);
      setErrorCode(normalized.code);
      if (normalized.code !== "FULFILLMENT_ORDER_REVISION_CONFLICT" || dirtyRef.current) return;
      try {
        const authoritative = await fetchFulfillmentWorkspaceEditor(request.workspaceId);
        if (
          workspaceIdRef.current !== request.workspaceId
          || requestSequenceRef.current !== request.sequence
          || dirtyRef.current
        ) return;
        setAuthoritativeEditor(authoritative);
        setErrorCode(normalized.code);
      } catch {
        // 保留原始 revision conflict，用户仍可通过页面重试加载。
      }
    },
  });

  const detailQuery = useQuery({
    queryKey: ["fulfillmentOrder", "detail", selectedOrderId],
    queryFn: () => fetchFulfillmentOrderDetail(selectedOrderId as string),
    enabled: Boolean(selectedOrderId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const adjustmentDetailQuery = useQuery({
    queryKey: ["fulfillmentOrder", "detail", selectedAdjustmentOrderId],
    queryFn: () => fetchFulfillmentOrderDetail(selectedAdjustmentOrderId as string),
    enabled: Boolean(selectedAdjustmentOrderId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const workspaceChangesQuery = useQuery({
    queryKey: ["fulfillmentOrder", "workspaceChanges", workspaceId],
    queryFn: () => listCourseRouteWorkspaceChanges(workspaceId),
    enabled: Boolean(workspaceId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const visibleLines = useMemo(
    () => filterFulfillmentLines(lines, filters, editor?.sourceDiff),
    [editor?.sourceDiff, filters, lines],
  );
  const localCurrentAmount = useMemo(() => {
    const ordersById = new Map(orders.map((order) => [order.id, order]));
    const payableAmounts = orders.map((order) => (
      editor?.workspace.status === "draft" && order.status === "draft"
        ? sumActiveFulfillmentLineAmounts(
          lines.filter((line) => line.fulfillmentOrderId === order.id),
        )
        : order.currentAmount
    ));
    const unassignedAmounts = lines
      .filter((line) => line.status === "active" && (
        !line.fulfillmentOrderId || !ordersById.has(line.fulfillmentOrderId)
      ))
      .map((line) => line.amount);
    return sumFulfillmentAmounts([...payableAmounts, ...unassignedAmounts]);
  }, [editor?.workspace.status, lines, orders]);
  const localUnassignedCount = useMemo(
    () => lines.filter((line) => line.status === "active" && !line.fulfillmentOrderId).length,
    [lines],
  );
  const settlementSupplierOptions = settlementSupplierQuery.data ?? [];
  const quickEntryFulfillmentSupplierOptions = quickEntrySupplierQuery.data ?? [];
  const settlementSupplierOptionsError = settlementSupplierQuery.error ?? null;
  const quickEntrySupplierOptionsError = quickEntrySupplierQuery.error ?? null;
  const settlementSupplierOptionsErrorCode = settlementSupplierOptionsError
    ? normalizeHttpError(settlementSupplierOptionsError).code
    : null;
  const quickEntrySupplierOptionsErrorCode = quickEntrySupplierOptionsError
    ? normalizeHttpError(quickEntrySupplierOptionsError).code
    : null;
  const supplierOptionsError =
    settlementSupplierOptionsError
      ?? quickEntrySupplierOptionsError
      ?? null;
  const supplierOptionsErrorCode = supplierOptionsError
    ? normalizeHttpError(supplierOptionsError).code
    : null;
  const refetchSettlementSupplierOptions = useCallback(async () => {
    await settlementSupplierQuery.refetch();
  }, [settlementSupplierQuery.refetch]);
  const refetchQuickEntrySupplierOptions = useCallback(async () => {
    if (quickEntryResourceType === "住宿") return;
    await quickEntrySupplierQuery.refetch();
  }, [quickEntryResourceType, quickEntrySupplierQuery.refetch]);
  const refetchSupplierOptions = useCallback(async () => {
    await Promise.all([
      refetchSettlementSupplierOptions(),
      refetchQuickEntrySupplierOptions(),
    ]);
  }, [
    refetchQuickEntrySupplierOptions,
    refetchSettlementSupplierOptions,
  ]);

  const save = useCallback(() => {
    const expectedRevision = draftRevisionRef.current ?? editor?.workspace.revision ?? "not-ready";
    return runWrite("save", JSON.stringify([workspaceId, expectedRevision]), () => {
      if (!canUpdate) {
        return Promise.reject(new FulfillmentOrderUserFacingError("当前账号无确认件修改权限"));
      }
      if (!editor) {
        return Promise.reject(new FulfillmentOrderUserFacingError("确认件工作区尚未就绪"));
      }
      const activeLines = lines.filter((line) => line.status === "active");
      const validationErrors = validateFulfillmentLinesBeforeSave(activeLines);
      if (validationErrors.length > 0) {
        return Promise.reject(new FulfillmentOrderUserFacingError(
          `保存前校验未通过：${validationErrors.join("；")}`,
        ));
      }
      requestSequenceRef.current += 1;
      return saveMutation.mutateAsync({
        workspaceId,
        sequence: requestSequenceRef.current,
        expectedRevision,
        lines: activeLines,
      });
    });
  }, [canUpdate, editor, lines, runWrite, saveMutation, workspaceId]);

  const generate = useCallback(() => {
    const expectedRevision = editor?.workspace.revision ?? "not-ready";
    return runWrite("generate", JSON.stringify([workspaceId, expectedRevision]), () => {
      if (dirtyRef.current) {
        return Promise.reject(new FulfillmentOrderUserFacingError("请先保存当前草稿，再生成协议"));
      }
      if (!canUpdate) {
        return Promise.reject(new FulfillmentOrderUserFacingError("当前账号无生成协议权限"));
      }
      if (!editor) {
        return Promise.reject(new FulfillmentOrderUserFacingError("确认件工作区尚未就绪"));
      }
      const activeLines = lines.filter((line) => line.status === "active");
      const activeUnassignedLines = activeLines.filter((line) => !line.fulfillmentOrderId);
      const settlementSupplierIds = new Set(
        activeUnassignedLines.map((line) => line.settlementSupplierId).filter(Boolean),
      );
      if (settlementSupplierIds.size !== 1) {
        return Promise.reject(new FulfillmentOrderUserFacingError("请选择结算主体后生成选中行协议"));
      }
      requestSequenceRef.current += 1;
      return generateMutation.mutateAsync({
        workspaceId,
        sequence: requestSequenceRef.current,
        payload: {
          expectedUpdatedAt: expectedRevision,
          lines: activeLines,
          selectedLineNos: activeUnassignedLines.map((line) => line.lineNo),
          settlementSupplierId: [...settlementSupplierIds][0] as string,
        },
      });
    });
  }, [canUpdate, editor, generateMutation, lines, runWrite, workspaceId]);

  const confirmHistoryLines = useCallback(() => {
    const expectedRevision = editor?.workspace.revision ?? "not-ready";
    return runWrite("confirm", JSON.stringify([workspaceId, expectedRevision]), async (isCurrent) => {
      if (!canUpdate) {
        throw new FulfillmentOrderUserFacingError("当前账号无确认件修改权限");
      }
      if (!editor) {
        throw new FulfillmentOrderUserFacingError("确认件工作区尚未就绪");
      }
      if (dirtyRef.current) {
        throw new FulfillmentOrderUserFacingError("请先保存当前明细，再确认历史数据");
      }
      const pendingLines = lines.filter((line) => (
        line.status === "active"
        && !line.fulfillmentOrderId
        && line.reviewStatus === "pending"
      ));
      if (pendingLines.length === 0) return editor;
      if (pendingLines.some((line) => line.dateMappingStatus !== "mapped")) {
        throw new FulfillmentOrderUserFacingError("仍有日期待调整明细，请逐行修正后再确认");
      }
      const authoritative = toLegacyFulfillmentEditor(await confirmCourseRouteLines(
        workspaceId,
        Number(expectedRevision),
      ));
      if (isCurrent()) setAuthoritativeEditor(authoritative);
      return authoritative;
    });
  }, [canUpdate, editor, lines, runWrite, setAuthoritativeEditor, workspaceId]);

  const finalize = useCallback(() => {
    const revision = Number(editor?.workspace.revision ?? 0);
    return runWrite("finalize", JSON.stringify([workspaceId, revision]), async (isCurrent) => {
      if (!canFinalize) throw new FulfillmentOrderUserFacingError("当前账号无确认件定稿权限");
      if (!editor) throw new FulfillmentOrderUserFacingError("确认件工作区尚未就绪");
      if (dirtyRef.current) throw new FulfillmentOrderUserFacingError("请先保存当前确认行，再定稿");
      const validationErrors = validateFulfillmentLinesBeforeFinalize(lines);
      if (validationErrors.length) throw new FulfillmentOrderUserFacingError(
        `定稿前校验未通过：${validationErrors.join("；")}`,
      );
      const authoritative = toLegacyFulfillmentEditor(
        await finalizeCourseRouteWorkspace(workspaceId, revision),
      );
      if (isCurrent()) setAuthoritativeEditor(authoritative);
      return authoritative;
    });
  }, [canFinalize, editor, lines, runWrite, setAuthoritativeEditor, workspaceId]);

  const unfinalize = useCallback(() => {
    const revision = Number(editor?.workspace.revision ?? 0);
    return runWrite("unfinalize", JSON.stringify([workspaceId, revision]), async (isCurrent) => {
      if (!canFinalize) throw new FulfillmentOrderUserFacingError("当前账号无取消定稿权限");
      if (!editor) throw new FulfillmentOrderUserFacingError("确认件工作区尚未就绪");
      if (dirtyRef.current) throw new FulfillmentOrderUserFacingError("请先处理当前未保存内容，再取消定稿");
      const authoritative = toLegacyFulfillmentEditor(
        await unfinalizeCourseRouteWorkspace(workspaceId, revision),
      );
      if (isCurrent()) setAuthoritativeEditor(authoritative);
      return authoritative;
    });
  }, [canFinalize, editor, runWrite, setAuthoritativeEditor, workspaceId]);

  const applyLineAdjustment = useCallback((
    lineId: string,
    adjustment: FulfillmentOrderLineAdjustment,
    reason: string,
  ) => {
    const target = lines.find((line) => line.id === lineId);
    const order = target?.fulfillmentOrderId
      ? orders.find((candidate) => candidate.id === target.fulfillmentOrderId)
      : null;
    const normalizedReason = reason.trim();
    const finalized = editor?.workspace.status !== "draft";
    const revision = order?.documentRevision
      ?? order?.currentVersionNo
      ?? Number(editor?.workspace.revision ?? 0);
    const key = JSON.stringify([
      workspaceId,
      target?.fulfillmentOrderId,
      lineId,
      adjustment.quantity,
      adjustment.billingCount,
      adjustment.specUnit,
      adjustment.unitPriceAmount,
      normalizedReason,
    ]);
    return runWrite("change", key, async (isCurrent) => {
      if (!canUpdate) throw new FulfillmentOrderUserFacingError("当前账号无确认件修改权限");
      if (!target || (!finalized && order?.status !== "effective")) {
        throw new FulfillmentOrderUserFacingError("仅已定稿确认件支持增减");
      }
      if (!revision) throw new FulfillmentOrderUserFacingError("确认件修订号缺失，请刷新后重试");
      if (!normalizedReason) throw new FulfillmentOrderUserFacingError("请填写增减说明");
      // 网络失败可能发生在提交后；后台版本刷新不改变用户意图，仍重放原完整请求。
      if (lineAdjustmentIntentRef.current?.key !== key) {
        lineAdjustmentIntentRef.current = {
          key,
          request: {
            expectedRevision: revision,
            idempotencyKey: createPostSigningIdempotencyKey(),
            reason: normalizedReason,

            addLines: [],
            adjustment: { sourceLineId: target.id, ...adjustment },
          },
        };
      }
      const intent = lineAdjustmentIntentRef.current;
      try {
        if (order) await applyFulfillmentOrderChange(order.id, intent.request);
        else await applyFinalizedCourseRouteWorkspaceChange(workspaceId, intent.request);
      } catch (error) {
        const normalized = normalizeHttpError(error);
        if (isCurrent()) {
          if (normalized.code === "FULFILLMENT_ORDER_REVISION_CONFLICT") {
            setErrorCode("FULFILLMENT_ORDER_REVISION_CONFLICT");
          }
          if (lineAdjustmentIntentRef.current === intent && !normalized.retriable
            && normalized.statusCode !== null && normalized.statusCode >= 400 && normalized.statusCode < 500) {
            lineAdjustmentIntentRef.current = null;
          }
        }
        throw error;
      }
      if (isCurrent() && lineAdjustmentIntentRef.current === intent) lineAdjustmentIntentRef.current = null;
      let authoritative = editor;
      try {
        authoritative = await fetchFulfillmentWorkspaceEditor(workspaceId);
        if (isCurrent()) setAuthoritativeEditor(authoritative);
      } catch {
        void queryClient.invalidateQueries({
          queryKey: fulfillmentWorkspaceQueryKey(workspaceId),
          exact: true,
        });
      }
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "detail"] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "workspaceChanges", workspaceId] }),
      ]);
      return authoritative;
    });
  }, [canUpdate, editor, lines, orders, queryClient, runWrite, setAuthoritativeEditor, workspaceId]);

  const generateSelected = useCallback((settlementSupplierId: string, lineIds?: readonly string[]) => {
    const expectedRevision = draftRevisionRef.current
      ?? editor?.workspace.revision
      ?? "not-ready";
    const targetIds = lineIds === undefined ? selectedIds : new Set(lineIds);
    const selectedActiveUnassignedLines = lines
      .filter((line) => targetIds.has(line.id)
        && line.status === "active"
        && !line.fulfillmentOrderId);
    // 显式分组必须整体有效；先校验再进入去重，避免失效集合复用合法子集的在途请求。
    if (lineIds !== undefined && (!settlementSupplierId.trim()
      || selectedActiveUnassignedLines.length !== targetIds.size
      || selectedActiveUnassignedLines.some(line => line.settlementSupplierId !== settlementSupplierId))) {
      return Promise.reject(new FulfillmentOrderUserFacingError("结算主体费用范围已变化，请刷新分组后重试"));
    }
    const selectedLineNos = selectedActiveUnassignedLines
      .map((line) => line.lineNo)
      .sort((left, right) => left - right);
    const key = JSON.stringify([
      workspaceId,
      expectedRevision,
      settlementSupplierId,
      selectedLineNos,
    ]);
    return runWrite("generate", key, async (isCurrent) => {
      if (!canUpdate) {
        return Promise.reject(new FulfillmentOrderUserFacingError("当前账号无生成协议权限"));
      }
      if (!editor) {
        return Promise.reject(new FulfillmentOrderUserFacingError("确认件工作区尚未就绪"));
      }
      if (selectedLineNos.length === 0) {
        return Promise.reject(new FulfillmentOrderUserFacingError("请先选择未归口确认行"));
      }
      const existingAgreement = orders.find(
        (order) => order.settlementSupplierId === settlementSupplierId,
      );
      if (existingAgreement && existingAgreement.status !== "draft") {
        return Promise.reject(new FulfillmentOrderUserFacingError(
          "该结算主体的服务协议已生效，请选择新的结算主体",
        ));
      }
      const activeLines = lines.filter((line) => line.status === "active");
      const validationErrors = selectedActiveUnassignedLines.flatMap((line) => {
        const prefix = `${fulfillmentLineIdentityLabel(line)}：`;
        const errors = validateFulfillmentLineBeforeGeneration(line);
        const guidePendingAssignment = line.resourceType.trim() === "导游"
          && line.providerAssignmentStatus === "unassigned";
        const stayResourceLinked = isFulfillmentStayResourceLinked(line);
        if (!guidePendingAssignment
          && !stayResourceLinked
          && (!line.fulfillmentSupplierId?.trim() || !line.fulfillmentSupplierName?.trim())) {
          errors.push("缺少服务供应商");
        }
        return errors.map((error) => `${prefix}${error}`);
      });
      if (validationErrors.length > 0) {
        return Promise.reject(new FulfillmentOrderUserFacingError(
          `生成前校验未通过：${validationErrors.join("；")}`,
        ));
      }
      requestSequenceRef.current += 1;
      try {
        return await generateMutation.mutateAsync({
          workspaceId,
          sequence: requestSequenceRef.current,
          payload: {
            expectedUpdatedAt: expectedRevision,
            lines: activeLines,
            selectedLineNos,
            settlementSupplierId,
          },
        });
      } catch (error) {
        // 明确的 CAS 冲突表示本次原子生成失败，不能凭其他人的归口结果推断成功。
        if (normalizeHttpError(error).code === "FULFILLMENT_ORDER_REVISION_CONFLICT") throw error;
        const authoritative = await fetchFulfillmentWorkspaceEditor(workspaceId);
        const orderById = new Map(authoritative.orders.map((order) => [order.id, order]));
        const generatedLineNos = new Set(authoritative.lines
          .filter((line) => {
            if (!line.fulfillmentOrderId) return false;
            return orderById.get(line.fulfillmentOrderId)?.settlementSupplierId
              === settlementSupplierId;
          })
          .map((line) => line.lineNo));
        if (!selectedLineNos.every((lineNo) => generatedLineNos.has(lineNo))) throw error;
        if (isCurrent()) setAuthoritativeEditor(authoritative);
        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
          queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "detail"] }),
        ]);
        return authoritative;
      }
    });
  }, [
    canUpdate,
    editor,
    generateMutation,
    lines,
    orders,
    queryClient,
    runWrite,
    selectedIds,
    setAuthoritativeEditor,
    workspaceId,
  ]);

  return {
    editor,
    setAuthoritativeEditor,
    quickEntryContext: editor?.quickEntryContext ?? null,
    quickEntryContextError: editor?.quickEntryContextError ?? null,
    searchTransportPoints,
    searchTransportResourceCandidates,
    searchResourceCandidates,
    lines,
    orders,
    visibleLines,
    filters,
    setFilters,
    selectedIds,
    setSelectedIds,
    selectableLineIds,
    isDirty,
    errorCode,
    isLoading: query.isPending || query.isPlaceholderData,
    loadError: query.error,
    refetch: query.refetch,
    reloadLatest,
    reloading: writeKind === "reload",
    canUpdate,
    localCurrentAmount,
    localUnassignedCount,
    supplierOptions: settlementSupplierOptions,
    settlementSupplierOptions,
    fulfillmentSupplierOptions: quickEntryFulfillmentSupplierOptions,
    quickEntryFulfillmentSupplierOptions,
    quickEntryResourceType,
    quickEntrySupplierSearch,
    setQuickEntryResourceType,
    searchSettlementSuppliers,
    searchFulfillmentSuppliers: searchQuickEntryFulfillmentSuppliers,
    searchQuickEntryFulfillmentSuppliers,
    supplierOptionsLoading:
      settlementSupplierQuery.isPending
      || quickEntrySupplierQuery.isPending,
    settlementSupplierOptionsLoading: settlementSupplierQuery.isPending,
    quickEntrySupplierOptionsLoading: quickEntrySupplierQuery.isPending,
    settlementSupplierOptionsError,
    quickEntrySupplierOptionsError,
    settlementSupplierOptionsErrorCode,
    quickEntrySupplierOptionsErrorCode,
    supplierOptionsError,
    supplierOptionsErrorCode,
    refetchSettlementSupplierOptions,
    refetchQuickEntrySupplierOptions,
    refetchSupplierOptions,
    updateLine,
    applyDraftBatch,
    addManualLines,
    addManualLine,
    addFinalizedLines,
    hasPendingFinalizedAddition,
    duplicateLine,
    removeLine,
    applyLineAdjustment,
    writePending: writeKind !== null,
    save,
    saving: writeKind === "save",
    saveError: saveMutation.error,
    generate,
    generateSelected,
    confirmHistoryLines,
    finalize,
    finalizing: writeKind === "finalize",
    unfinalize,
    unfinalizing: writeKind === "unfinalize",
    confirmingHistoryLines: writeKind === "confirm",
    changingPostSigningLine: writeKind === "change",
    generating: writeKind === "generate",
    generateError: generateMutation.error,
    selectedOrderId,
    selectOrder: setSelectedOrderId,
    orderDetail: detailQuery.data ?? null,
    orderDetailLoading: detailQuery.isFetching && Boolean(selectedOrderId),
    orderDetailError: detailQuery.error,
    refetchOrderDetail: detailQuery.refetch,
    selectedAdjustmentOrderId,
    selectAdjustmentOrder: setSelectedAdjustmentOrderId,
    adjustmentDetail: adjustmentDetailQuery.data ?? null,
    adjustmentDetailLoading:
      adjustmentDetailQuery.isFetching && Boolean(selectedAdjustmentOrderId),
    adjustmentDetailError: adjustmentDetailQuery.error,
    refetchAdjustmentDetail: adjustmentDetailQuery.refetch,
    workspaceChanges: workspaceChangesQuery.data ?? [],
    workspaceChangesLoading: workspaceChangesQuery.isFetching,
    workspaceChangesError: workspaceChangesQuery.error,
    refetchWorkspaceChanges: workspaceChangesQuery.refetch,
  };
}
